// Редовете на фактурата в модала за издаване — състоянието и действията с тях
// (изнесени от `InvoiceCreateModal` при фактурата за депозит). Чистата логика
// (строене, редакция, обобщаване) е в `invoiceLines.ts`; тук е само кога се
// пълнят и как се пипат.

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { BillHistory } from "../../../../../api/reportsBillsHistory";
import { useMenuItems, useMenuVats } from "../../../../../hooks/useMenu";
import { depositInvoiceLine, type DepositInvoiceSource } from "./depositInvoice";
import {
  buildInvoiceLines,
  patchInvoiceLine,
  recalcLineTotal,
  removeInvoiceLine,
  summarizeInvoiceLines,
  type Line,
  type LineDraft,
} from "./invoiceLines";

export type SummarizeMode = "per-item" | "single";

const newLocalId = () => `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

/**
 * Lines държим в local state — операторът може да ги редактира, изтрива, да
 * добавя нови и да „обобщава" сметката. Първоначално се пълнят от сметките
 * (фактура по сметки), празни (свободна) или с реда на аванса (за депозит),
 * но след това не се re-derivat-ват от тях.
 */
export function useInvoiceLines({
  open,
  free,
  deposit,
  bills,
}: {
  open: boolean;
  free: boolean;
  deposit: DepositInvoiceSource | null;
  bills: BillHistory[];
}) {
  const { t } = useTranslation();
  const items = useMenuItems();
  const vats = useMenuVats();

  const itemNameById = useMemo(() => {
    const names = new Map<number, { name: string; dds: number | null }>();
    items.forEach((item) => {
      const lang = item.itemLangs.find((itemLang) => itemLang.languageCode === "bg");
      names.set(item.id, {
        name: lang?.name ?? item.slug ?? `#${item.id}`,
        dds: item.dds ?? null,
      });
    });
    return names;
  }, [items]);

  const vatRateById = useMemo(() => {
    const rates = new Map<number, number>();
    vats.forEach((vat) => rates.set(vat.id, vat.vatRate));
    return rates;
  }, [vats]);

  const [lines, setLines] = useState<Line[]>([]);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  // Summarize state — модалът помни в кой режим сме и може да се върне
  // обратно. При „single" пазим snapshot на предишните lines.
  const [summarizeMode, setSummarizeMode] = useState<SummarizeMode>("per-item");
  const [linesBeforeSummary, setLinesBeforeSummary] = useState<Line[] | null>(null);

  // Init / reset на lines при отваряне или смяна на bills: продуктите и
  // добавките им, после по един заключен ред за бакшиш на сметка с бакшиш и
  // по един заключен отрицателен ред за сметка, платена с депозит.
  useEffect(() => {
    if (!open || free || deposit) return;
    const out = buildInvoiceLines(bills, itemNameById, vatRateById);
    setLines(out);
    setSelectedLineId(out[0]?.id ?? null);
    setSummarizeMode("per-item");
    setLinesBeforeSummary(null);
  }, [open, free, deposit, bills, itemNameById, vatRateById]);
  // Свободната започва празна, а тази за депозит — с реда на аванса; само при
  // отваряне: опреснено меню (фокус, обедният часовник) не бива да трие
  // написаното на ръка.
  useEffect(() => {
    if (!open || (!free && !deposit)) return;
    const initialLines = deposit ? [depositInvoiceLine(deposit)] : [];
    setLines(initialLines);
    setSelectedLineId(initialLines[0]?.id ?? null);
    setSummarizeMode("per-item");
    setLinesBeforeSummary(null);
  }, [open, free, deposit]);

  // total винаги се изчислява спрямо qty * unitPrice — никога не се
  // редактира директно. Заключените редове не се пипат (`invoiceLines.ts`).
  const patchLine = (id: string, patch: Partial<LineDraft>) => {
    setLines((prev) => patchInvoiceLine(prev, id, patch));
  };

  const removeLine = (id: string) => {
    setLines((prev) => {
      const next = removeInvoiceLine(prev, id);
      // Ако сме изтрили избрания, селекция отива на следващия наличен.
      if (selectedLineId === id && next.length !== prev.length) {
        setSelectedLineId(next[0]?.id ?? null);
      }
      return next;
    });
  };

  const addLine = (data: LineDraft) => {
    const line: Line = { ...data, id: newLocalId(), total: recalcLineTotal(data) };
    setLines((prev) => [...prev, line]);
    setSelectedLineId(line.id);
  };

  const applySummarizeMode = (nextMode: SummarizeMode) => {
    if (nextMode === summarizeMode) return;
    if (nextMode === "single") {
      if (lines.length === 0) return;
      // Snapshot преди collapse — за да може user-ът да върне „По име и цена".
      setLinesBeforeSummary(lines);
      // Един ред „Консумация" на ДДС ставка; бакшишът и депозитът остават отделни.
      const collapsedLines = summarizeInvoiceLines(
        lines,
        t("invoiceCreate.summarizedName"),
        newLocalId,
      );
      setLines(collapsedLines);
      setSelectedLineId(collapsedLines[0]?.id ?? null);
      setSummarizeMode("single");
    } else {
      // Връщане към per-item: ако имаме snapshot — restore. Иначе оставяме
      // текущите редове (необичаен случай, но защитен).
      if (linesBeforeSummary) {
        setLines(linesBeforeSummary);
        setSelectedLineId(linesBeforeSummary[0]?.id ?? null);
      }
      setLinesBeforeSummary(null);
      setSummarizeMode("per-item");
    }
  };

  return {
    lines,
    selectedLineId,
    setSelectedLineId,
    summarizeMode,
    patchLine,
    removeLine,
    addLine,
    applySummarizeMode,
  };
}
